import { TIER_ORDER } from 'components/Modals/BundleBuilderModal/bundleBuilderConfig';
import { BundleBuilderCatalog, BundleBuilderEntry } from 'components/Modals/BundleBuilderModal/useBundleBuilderCatalog';
import { useCallback, useEffect, useMemo, useState } from 'react';

type Tier = BundleBuilderEntry['tier'];

export type MiniBuilderSeed = { tier: string | null; memberSlugs: string[] } | null;

/**
 * Selection state for the compact Bundle Builder on Add new course.
 *
 * The rules are the full builder's (BundleBuilderContent), so a pick made here resolves to the same
 * package there: one representative bundle per tier describes the package, CPT is locked in every
 * package except Master (where everything is), and the package size comes from the bundle's real
 * membership rather than the tier name.
 *
 * Two differences, both because this surface is smaller:
 * - a one-slot package (Essential) swaps on click instead of making the learner deselect first;
 * - `focusCert` lets a certification card further down the page drop its cert into the box.
 */
export const useMiniBundleBuilder = (catalog: BundleBuilderCatalog | null, seed: MiniBuilderSeed) => {
  const [selectedTier, setSelectedTier] = useState<Tier | null>(null);
  const [picks, setPicks] = useState<string[]>([]);

  const tiers = useMemo(() => {
    const byTier = new Map<Tier, BundleBuilderEntry>();
    for (const bundle of catalog?.bundles ?? []) {
      if (!byTier.has(bundle.tier)) byTier.set(bundle.tier, bundle);
    }
    return TIER_ORDER.filter((t) => byTier.has(t)).map((t) => byTier.get(t) as BundleBuilderEntry);
  }, [catalog]);

  const certById = useMemo(() => new Map((catalog?.certs ?? []).map((cert) => [cert.courseId, cert])), [catalog]);

  const active = useMemo(() => tiers.find((t) => t.tier === selectedTier) ?? null, [tiers, selectedTier]);
  const isMaster = active?.tier === 'master';

  const lockedIds = useMemo(() => {
    if (!active) return [];
    if (isMaster) return active.memberCourseIds;
    return active.primaryCourseId ? [active.primaryCourseId] : [];
  }, [active, isMaster]);

  const certCount = active?.memberCourseIds.length ?? 0;
  const slots = Math.max(0, certCount - lockedIds.length);

  const defaultPicks = useCallback((entry: BundleBuilderEntry) => {
    if (entry.tier === 'master') return [];
    return entry.memberCourseIds.filter((id) => id !== entry.primaryCourseId);
  }, []);

  // Open on the learner's trial bundle when they have one, otherwise on the first package with its
  // contents filled in — the picker never starts empty.
  useEffect(() => {
    if (selectedTier || !tiers.length || !catalog) return;

    if (seed) {
      const target =
        tiers.find((t) => t.tier === seed.tier) ??
        tiers.find((t) => t.memberCourseIds.length === seed.memberSlugs.length);

      if (target) {
        const idBySlug = new Map(catalog.certs.map((cert) => [cert.slug, cert.courseId]));
        const primary = target.tier === 'master' ? null : target.primaryCourseId;
        const seeded = seed.memberSlugs
          .map((slug) => idBySlug.get(slug))
          .filter((id): id is string => Boolean(id) && id !== primary);
        const slotsForTarget = target.memberCourseIds.length - (primary ? 1 : 0);

        setSelectedTier(target.tier);
        setPicks(target.tier !== 'master' && seeded.length === slotsForTarget ? seeded : defaultPicks(target));
        return;
      }
    }

    setSelectedTier(tiers[0].tier);
    setPicks(defaultPicks(tiers[0]));
  }, [tiers, selectedTier, catalog, seed, defaultPicks]);

  const chooseTier = (entry: BundleBuilderEntry) => {
    if (entry.tier === selectedTier) return;
    setSelectedTier(entry.tier);
    setPicks(defaultPicks(entry));
  };

  const togglePick = (courseId: string) => {
    if (lockedIds.includes(courseId)) return;
    setPicks((current) => {
      if (current.includes(courseId)) return current.filter((id) => id !== courseId);
      if (current.length < slots) return [...current, courseId];
      if (slots === 1) return [courseId];
      return current;
    });
  };

  /** Puts a certification in the box without changing package: fills a free slot, else swaps the newest pick. */
  const focusCert = (courseId: string) => {
    if (!certById.has(courseId) || lockedIds.includes(courseId) || slots === 0) return;
    setPicks((current) => {
      if (current.includes(courseId)) return current;
      if (current.length < slots) return [...current, courseId];
      return [...current.slice(0, -1), courseId];
    });
  };

  const selectedIds = useMemo(() => [...lockedIds, ...picks], [lockedIds, picks]);
  const isComplete = Boolean(active) && picks.length === slots;

  /**
   * The exact package these picks make, matched locally so the summary can name it and price it
   * before the learner clicks. Checkout still resolves on the server, which is the authority.
   */
  const matchedBundle = useMemo(() => {
    if (!isComplete) return null;
    const wanted = [...selectedIds].sort().join('|');
    return (catalog?.bundles ?? []).find((b) => [...b.memberCourseIds].sort().join('|') === wanted) ?? null;
  }, [catalog, selectedIds, isComplete]);

  return {
    tiers,
    active,
    isMaster,
    lockedIds,
    picks,
    slots,
    certCount,
    selectedIds,
    isComplete,
    matchedBundle,
    certById,
    chooseTier,
    togglePick,
    focusCert,
  };
};

export type MiniBundleBuilder = ReturnType<typeof useMiniBundleBuilder>;
